import { formatAnyDateToLocale } from "./dateUtils";

function formatBackendDates(message: string): string {
  return message.replace(/\b\d{4}-\d{2}-\d{2}\b/g, (ymd) =>
    formatAnyDateToLocale(ymd)
  );
}

export type ApiDetailOutcome = { kind: "raw"; text: string } | { kind: "generic" };

export function extractApiDetailOutcome(err: any): ApiDetailOutcome {
  const detail = err?.response?.data?.detail;

  if (typeof detail === "string") return { kind: "raw", text: formatBackendDates(detail) };

  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d: any) => d?.msg || (d?.type ? String(d.type) : null))
      .filter(Boolean);

    if (msgs.length) return { kind: "raw", text: formatBackendDates(msgs.join("; ")) };
  }

  if (detail && typeof detail === "object") {
    if (detail.message) return { kind: "raw", text: formatBackendDates(String(detail.message)) };
    if (detail.msg) return { kind: "raw", text: formatBackendDates(String(detail.msg)) };
  }

  return err?.message
    ? { kind: "raw", text: formatBackendDates(err.message) }
    : { kind: "generic" };
}


export function extractApiDetail(err: any): string {
  const outcome = extractApiDetailOutcome(err);
  return outcome.kind === "raw" ? outcome.text : "Something went wrong. Please try again.";
}

/**
 * Raw detail text used ONLY for closed-period phrase matching -- a
 * narrower, dedicated extraction than extractApiDetail's (which also
 * joins array-shaped validation details, reads object.message/object.msg,
 * and reformats embedded dates). The backend's own closed-period detail
 * (utils/accounting_guards.py's _assert_not_closed, raised by both
 * assert_year_open and assert_source_not_closed) is always a plain
 * string, so this simpler string-or-message-or-empty chain is sufficient
 * and keeps this detector's behavior independent of extractApiDetail's
 * broader formatting concerns.
 */
function closedPeriodDetailText(err: any): string {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (typeof err?.message === "string") return err.message;
  return "";
}

/**
 * Phrases that identify a closed-fiscal-period rejection.
 *
 * - "closed fiscal period": the ACTUAL backend wording, produced by every
 *   _assert_not_closed call site (assert_year_open's "Posting date ..." /
 *   assert_source_not_closed's "Source transaction date ..."), e.g.
 *   "Posting date is in a closed fiscal period (closed through
 *   2025-06-30).". Confirmed via repository search to be the ONLY
 *   closed-period detail text this backend produces.
 * - "closed period": legacy/simplified phrasing, kept for backward
 *   compatibility -- no current backend caller emits this exact text, but
 *   removing it is not proven safe by repository search (existing tests
 *   and any historical/alternate wording may still rely on it), and
 *   keeping it cannot introduce a false positive since it is a strict
 *   textual subset of the concern this function detects.
 * - "closed_through_date" / "period is closed": pre-existing legacy
 *   checks, retained unchanged for the same reason.
 *
 * Deliberately explicit substrings, not a broad regex on the word
 * "period" alone -- must NOT match IdempotencyRequestConflict messages,
 * generic HTTP 409 responses, or unrelated text that merely mentions
 * "period" (e.g. a grace period, a reporting period).
 */
const CLOSED_PERIOD_PHRASES = [
  "closed fiscal period",
  "closed period",
  "closed_through_date",
  "period is closed",
];

export function isClosedPeriodError(err: any): boolean {
  const msg = closedPeriodDetailText(err).toLowerCase();
  return CLOSED_PERIOD_PHRASES.some((phrase) => msg.includes(phrase));
}

export function formatClosedThroughDate(value?: string | null): string {
  if (!value) return "the closed date";
  return formatAnyDateToLocale(value.slice(0, 10));
}

export function isDateClosed(
  ymd?: string | null,
  closedThroughDate?: string | null
): boolean {
  if (!ymd || !closedThroughDate) return false;
  return ymd.slice(0, 10) <= closedThroughDate.slice(0, 10);
}

export function closedPeriodSubmitMessage(
  closedThroughDate?: string | null
): string {
  return `This transaction date is in a closed period. Please use a date after ${formatClosedThroughDate(
    closedThroughDate
  )}.`;
}

export const CLOSED_PERIOD_WARNING_MSG =
  "This period is closed. Please use a date after the closed year.";

export const CLOSED_PERIOD_EDIT_BLOCK_MSG =
  "This record is in a closed period and cannot be changed.";